import React, { useMemo } from 'react';
import { AlertTriangle, Eye, ListFilter, Shuffle, X } from 'lucide-react';
import type { TeamMemberWorkload, UnifiedWorkItem } from '../../../modules/minha-mesa/types/workItem';
import {
  WORK_SOURCE_COLORS,
  WORK_SOURCE_LABELS,
  WORK_STATUS_COLORS,
  WORK_STATUS_LABELS,
} from '../../../modules/minha-mesa/types/workItem';
import {
  MAX_ACTIVE_TASKS_PER_OPERATOR,
  SOURCE_BORDER,
  activitySortKey,
  getInitials,
} from './teamCapacityShared';
import { OCCUPANCY_BADGE_CLASS, getOccupancyTier } from '../executive/opsExecutiveShared';
import { operatorLoadLabel } from '../executive/opsTeamMetrics';

interface TeamCapacityActivitiesPanelProps {
  userId: string;
  member: TeamMemberWorkload;
  items: UnifiedWorkItem[];
  onClose: () => void;
  embedded?: boolean;
}

export const TeamCapacityActivitiesPanel: React.FC<TeamCapacityActivitiesPanelProps> = ({
  userId,
  member,
  items,
  onClose,
  embedded = false,
}) => {
  const userItems = useMemo(
    () => items
      .filter((i) => i.assigned_to === userId && i.status !== 'completed')
      .sort((a, b) => activitySortKey(a) - activitySortKey(b)),
    [items, userId],
  );

  const occupationPct = Math.round((member.total_active / MAX_ACTIVE_TASKS_PER_OPERATOR) * 100);
  const loadTier = getOccupancyTier(occupationPct);
  const loadBadge = OCCUPANCY_BADGE_CLASS[loadTier];

  return (
    <div className={`${embedded ? 'mx-2 mb-2' : 'mt-1'} rounded-sm border border-[var(--border-default)]/70 bg-[var(--bg-surface)] overflow-hidden`}>
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-[var(--border-default)]/60 bg-[var(--bg-elevated)]/80 px-3 py-2">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <div className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-sm text-[10px] font-bold ${
            loadTier === 'critical' ? 'bg-red-100 text-red-800 dark:bg-red-500/20' : 'bg-[var(--bg-elevated)] text-[var(--text-secondary)]'
          }`}>
            {getInitials(member.user_name)}
          </div>
          <div>
            <div className="text-[12px] font-bold text-slate-800 dark:text-slate-100">{member.user_name}</div>
            <div className="text-[9px] text-[var(--text-muted)]">{member.role_label ?? '—'} · Ocupação {occupationPct}%</div>
          </div>
          <span className={`rounded-sm px-1.5 py-0.5 text-[8px] font-bold ${loadBadge.bg} ${loadBadge.text}`}>
            {operatorLoadLabel(occupationPct)}
          </span>
          {member.sla_violated_pct > 0 && (
            <span className="flex items-center gap-0.5 text-[9px] font-semibold text-red-600">
              <AlertTriangle size={9} />
              SLA {member.sla_violated_pct}% violado
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            title="Redistribuir tarefas"
            className="flex items-center gap-1 rounded-sm border border-[var(--border-default)] px-2 py-1 text-[8px] font-semibold text-[var(--text-secondary)] hover:bg-[var(--button-hover)]"
          >
            <Shuffle size={9} /> Redistribuir
          </button>
          <button
            type="button"
            title="Priorizar fila"
            className="flex items-center gap-1 rounded-sm border border-[var(--border-default)] px-2 py-1 text-[8px] font-semibold text-[var(--text-secondary)] hover:bg-[var(--button-hover)]"
          >
            <ListFilter size={9} /> Priorizar
          </button>
          <button
            type="button"
            title="Ver gargalo"
            className="flex items-center gap-1 rounded-sm border border-[var(--border-default)] px-2 py-1 text-[8px] font-semibold text-[var(--text-secondary)] hover:bg-[var(--button-hover)]"
          >
            <Eye size={9} /> Gargalo
          </button>
          <button type="button" onClick={onClose} className="ml-1 rounded-sm p-1 hover:bg-[var(--button-hover)]">
            <X size={12} className="text-[var(--text-muted)]" />
          </button>
        </div>
      </div>

      <div className="px-3 py-2">
        <h4 className="mb-1.5 text-[9px] font-bold uppercase tracking-wider text-slate-800 dark:text-slate-100">
          Atividades ({userItems.length})
        </h4>
        <div className="max-h-[200px] space-y-1 overflow-y-auto">
          {userItems.map((item) => (
            <div
              key={item.id}
              className={`flex items-center gap-2 rounded-sm border-l-[3px] border px-2 py-1.5 text-[10px] ${
                SOURCE_BORDER[item.source] ?? 'border-l-[var(--border-default)]'
              } ${
                item.is_overdue
                  ? 'border-red-200 bg-red-50/60'
                  : item.status === 'in_progress'
                  ? 'border-blue-100 bg-blue-50/40'
                  : 'border-[var(--border-default)] bg-[var(--bg-surface)]'
              }`}
            >
              <span className={`shrink-0 rounded px-1 py-0.5 text-[7px] font-semibold ${WORK_SOURCE_COLORS[item.source]}`}>
                {WORK_SOURCE_LABELS[item.source].slice(0, 3)}
              </span>
              <span className={`shrink-0 rounded px-1 py-0.5 text-[7px] font-semibold ${WORK_STATUS_COLORS[item.status]}`}>
                {WORK_STATUS_LABELS[item.status]}
              </span>
              {item.is_overdue && (
                <span className="shrink-0 rounded bg-red-100 px-1 py-0.5 text-[7px] font-bold text-red-700">ATRASO</span>
              )}
              <span className="min-w-0 flex-1 truncate font-medium text-slate-800 dark:text-slate-100">{item.title}</span>
            </div>
          ))}
          {userItems.length === 0 && (
            <p className="py-2 text-[9px] italic text-[var(--text-muted)]">Nenhuma tarefa ativa</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default TeamCapacityActivitiesPanel;
